/**
 * PublicImpactTicker — horizontal marquee showing the network's live
 * impact totals + recent named actions on the SocialCapital home page.
 *
 * Two data feeds:
 *   1. /social-capital/impact-summary — the 6 headline counters
 *   2. /social-capital/recent-actions — named "Meera planted 3 trees · 2m ago"
 *      rows for a human-scale story that keeps returning members feeling
 *      the momentum.
 *
 * Both refetch every 60s and are interleaved into a single loop.
 */
import { useEffect, useState } from "react";
import { Heart, Utensils, Trees, Users, Repeat, BookOpen, Sparkles } from "lucide-react";
import { http } from "@/lib/api";

const METRICS = [
  { key: "people_helped",    label: "People helped",     Icon: Heart },
  { key: "meals_rescued",    label: "Meals rescued",     Icon: Utensils },
  { key: "trees_planted",    label: "Trees planted",     Icon: Trees },
  { key: "volunteer_events", label: "Volunteer events",  Icon: Users },
  { key: "items_reused",     label: "Items reused",      Icon: Repeat },
  { key: "knowledge_shared", label: "Knowledge shared",  Icon: BookOpen },
];

function fmt(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000)     return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return v.toLocaleString();
}

export default function PublicImpactTicker({ testid = "impact-ticker" }) {
  const [totals, setTotals] = useState(null);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      http.get("/social-capital/impact-summary")
        .then((r) => { if (alive) setTotals(r.data); })
        .catch(() => {});
      http.get("/social-capital/recent-actions?limit=8")
        .then((r) => { if (alive) setActions(r.data?.items || []); })
        .catch(() => { if (alive) setActions([]); });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Build one interleaved row of counters + named actions. Named actions
  // slot in between each pair of counters so the ticker feels alive
  // without letting the anchors (the counters) drift out of view.
  const items = [];
  METRICS.forEach((m, i) => {
    items.push({ kind: "metric", ...m, value: totals ? fmt(totals[m.key]) : "…" });
    // Push one action between each pair of counters.
    const act = actions[i];
    if (act) items.push({ kind: "action", ...act });
  });
  // If there are still leftover actions (more than counters), append them.
  actions.slice(METRICS.length).forEach((a) => items.push({ kind: "action", ...a }));

  const row = (
    <div className="flex items-center gap-10 px-6 shrink-0">
      {items.map((it, idx) => it.kind === "metric" ? (
        <div key={`m${idx}`} className="inline-flex items-center gap-2 whitespace-nowrap"
             data-testid={`${testid}-metric-${it.key}`}>
          <it.Icon className="w-4 h-4 opacity-80" aria-hidden />
          <span className="font-display font-bold text-lg tabular-nums">{it.value}</span>
          <span className="text-xs uppercase tracking-wider opacity-75">{it.label}</span>
        </div>
      ) : (
        <div key={`a${idx}`} className="inline-flex items-center gap-2 whitespace-nowrap opacity-95"
             data-testid={`${testid}-action-${it.category}`}>
          {it.avatar_url ? (
            <img src={it.avatar_url} alt=""
                 className="w-6 h-6 rounded-full object-cover ring-1 ring-white/30"
                 loading="lazy" aria-hidden />
          ) : (
            <span
              className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ring-1 ring-white/30"
              style={{ background: "rgba(255,255,255,0.18)", color: "#F0FDF4" }}
              aria-hidden
            >{(it.name || "?").charAt(0).toUpperCase()}</span>
          )}
          <span className="text-sm">
            <b>{it.name}</b> {it.verb}
            {it.reason ? <span className="opacity-80"> — {it.reason}</span> : null}
          </span>
          <span className="text-[11px] uppercase tracking-wider opacity-70">{it.ago}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className="relative overflow-hidden rounded-full py-3 my-8"
      style={{
        background: "linear-gradient(90deg,#065F46 0%,#059669 55%,#10B981 100%)",
        color: "#F0FDF4",
      }}
      data-testid={testid}
      role="marquee"
      aria-label="Live network impact totals"
    >
      <div className="ticker-track flex items-center gap-10">
        {row}
        {row}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16"
           style={{ background: "linear-gradient(to right, #065F46, transparent)" }} />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16"
           style={{ background: "linear-gradient(to left, #10B981, transparent)" }} />
      <style>{`
        .ticker-track {
          animation: sc-ticker 90s linear infinite;
          width: max-content;
        }
        @keyframes sc-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation-duration: 360s; }
        }
      `}</style>
    </div>
  );
}
